/**
 * The figures the Organisation page's panels and hero read beyond the raw responses: each row a panel ranks, its change on last
 * month, and the one finding each panel leads with. Kept apart from the components so the arithmetic can be tested on its own.
 */
import type {
  BudgetReportResponse,
  BudgetReportUnitResponse,
  GroupDailySpendSeriesResponse,
  OrganisationDailySpendResponse,
  OrganisationDailySpendSeriesResponse,
  OrganisationPersonUsageRow,
  OrganisationPersonUsageVendor,
  OrganisationSpendResponse,
  OrganisationTrendMonthResponse,
  OrganisationTrendResponse,
  OrganisationUnitSpendResponse,
  ToolComparisonResponse,
} from "../../api/organisationReporting";
import { unclassifiedLabel } from "../overview/overviewFigures";

/** The ids of the sections a "worth a look" item can point at. */
export const sectionIds = {
  tool: "organisation-tool",
  model: "organisation-model",
  division: "organisation-division",
  department: "organisation-department",
  group: "organisation-group",
  person: "organisation-person",
  budget: "organisation-budget",
  seats: "organisation-seats",
} as const;

/** One row a panel ranks: a tool, a model, a unit or a person, with the figures beside its bar. */
export interface RankedRow {
  /** Unique within its panel. */
  key: string;
  label: string;
  /** The period's spend, seat fees included; null when it could not be converted. */
  spend: number | null;
  /** Its share of the panel's whole spend, as a percentage. */
  sharePercent: number;
  /** The people it counts, or null where people are not the point (a model). */
  people: number | null;
  /** Its spend per person, or null where there are no people to divide by. */
  perPerson: number | null;
  /** The change in its usage on the same days of last month, as a fraction (0.25 is 25% up), or null when there is nothing to compare with. */
  change: number | null;
  /** Its usage over the same days of last month, the base of `change`; null when not known. */
  previousUsage?: number | null;
  /** A short note beneath its bar: a tool's take-up, a person's main tool. */
  detail?: string | null;
  /** The page the row opens: a unit's or group's page, or a person's; none when there is no page or the viewer may not open it. */
  href?: string | null;
}

/**
 * The smallest usage, in the reporting currency, a change is measured from. Below it a percentage says more about the size of the
 * base than about any change, so none is shown.
 */
export const minimumComparableSpend = 20;

/**
 * The smallest base, in the reporting currency, a rise is reported from in a finding. A row can carry a change from a smaller base,
 * but a model new last month going from $20 to $600 is not news in the way a department doubling is.
 */
export const minimumRiseBase = 200;

/**
 * How many days of each month are compared with last month. A month still running is compared over its complete days, today's
 * being only part of one, against the same days of the month before; a month that has ended is compared whole. Zero means
 * there is nothing to compare yet (the 1st of the month).
 *
 * @param periodStart - The month's first day, `YYYY-MM-DD`.
 * @param generatedAt - When the figures were read.
 * @param isCurrentMonth - Whether the month is still running.
 * @returns The last day of the month to compare, as a day of the month.
 */
export function comparisonDays(periodStart: string, generatedAt: string, isCurrentMonth: boolean): number {
  if (!isCurrentMonth) {
    return 31;
  }

  const generated = new Date(generatedAt);
  const start = new Date(`${periodStart}T00:00:00Z`);
  if (generated.getUTCFullYear() !== start.getUTCFullYear() || generated.getUTCMonth() !== start.getUTCMonth()) {
    return 31;
  }

  return generated.getUTCDate() - 1;
}

/** The day of the month of a `YYYY-MM-DD` date. */
function dayOfMonth(date: string): number {
  return Number(date.slice(8, 10));
}

/**
 * Each unit's usage over the first `throughDay` days of a month, from a grouping's daily series.
 *
 * @param series - The daily series, or null.
 * @param throughDay - The last day of the month to count.
 * @returns The usage by unit label.
 */
export function unitUsageThrough(series: OrganisationDailySpendSeriesResponse | null, throughDay: number): Map<string, number> {
  const totals = new Map<string, number>();
  for (const day of series?.days ?? []) {
    if (dayOfMonth(day.date) > throughDay) {
      continue;
    }
    for (const unit of day.units) {
      totals.set(unit.unit, (totals.get(unit.unit) ?? 0) + unit.amount);
    }
  }

  return totals;
}

/**
 * Each tool's usage over the first `throughDay` days of a month, from the organisation's daily series by tool.
 *
 * @param series - The daily series by tool, or null.
 * @param throughDay - The last day of the month to count.
 * @returns The usage by vendor id, lower-cased.
 */
export function toolUsageThrough(series: GroupDailySpendSeriesResponse | null, throughDay: number): Map<string, number> {
  const totals = new Map<string, number>();
  for (const day of series?.days ?? []) {
    if (dayOfMonth(day.date) > throughDay) {
      continue;
    }
    for (const vendor of day.vendors) {
      const key = vendor.vendorId.toLowerCase();
      totals.set(key, (totals.get(key) ?? 0) + vendor.amount);
    }
  }

  return totals;
}

/**
 * The change from last month's usage to this month's, as a fraction, or null when either side is too small to compare.
 *
 * @param current - This month's usage over the compared days.
 * @param previous - Last month's usage over the same days.
 * @returns The change, or null.
 */
export function changeBetween(current: number | undefined, previous: number | undefined): number | null {
  if (previous === undefined || previous < minimumComparableSpend || current === undefined) {
    return null;
  }

  return (current - previous) / previous;
}

/** The share of a whole, as a percentage, or 0 when the whole is nothing. */
function shareOf(spend: number | null, total: number): number {
  return total > 0 && spend !== null ? (spend / total) * 100 : 0;
}

/** The usage on each side of the comparison, by the key the rows are matched on; null when last month's figures are not loaded. */
export interface UsageComparison {
  current: Map<string, number>;
  previous: Map<string, number> | null;
}

/**
 * A division, department or group panel's rows, largest first. Units with no spend are left out.
 *
 * @param units - The units, as the organisation spend reports them.
 * @param usage - Each unit's usage this month and last, over the compared days.
 * @param canOpen - Whether the viewer may open a unit's page, so the rows link to them.
 * @returns The rows.
 */
export function unitRows(units: OrganisationUnitSpendResponse[], usage: UsageComparison, canOpen = false): RankedRow[] {
  const total = units.reduce((sum, unit) => sum + (unit.totalSpend ?? 0), 0);
  return units
    .filter((unit) => (unit.totalSpend ?? 0) > 0)
    .sort((a, b) => (b.totalSpend ?? 0) - (a.totalSpend ?? 0))
    .map((unit) => ({
      key: unit.unitLabel,
      label: unit.unitLabel,
      spend: unit.totalSpend,
      sharePercent: shareOf(unit.totalSpend, total),
      people: unit.personCount,
      perPerson: unit.totalSpend !== null && unit.personCount > 0 ? unit.totalSpend / unit.personCount : null,
      change: usage.previous ? changeBetween(usage.current.get(unit.unitLabel), usage.previous.get(unit.unitLabel)) : null,
      previousUsage: usage.previous?.get(unit.unitLabel) ?? null,
      href: canOpen && unit.unitId ? `/my-groups/${encodeURIComponent(unit.unitId)}` : null,
    }));
}

/**
 * The model panel's rows, largest first: each model's usage summed from the daily series (models carry no seat fee).
 *
 * @param byModel - This month's daily series by model, or null.
 * @param usage - Each model's usage this month and last, over the compared days.
 * @returns The rows.
 */
export function modelRows(byModel: OrganisationDailySpendSeriesResponse | null, usage: UsageComparison): RankedRow[] {
  const totals = unitUsageThrough(byModel, 31);
  const total = [...totals.values()].reduce((sum, spend) => sum + spend, 0);
  return [...totals.entries()]
    .filter(([, spend]) => spend > 0)
    .sort((a, b) => b[1] - a[1])
    .map(([label, spend]) => ({
      key: label,
      label,
      spend,
      sharePercent: shareOf(spend, total),
      people: null,
      perPerson: null,
      change: usage.previous ? changeBetween(usage.current.get(label), usage.previous.get(label)) : null,
      previousUsage: usage.previous?.get(label) ?? null,
    }));
}

/**
 * The tool panel's rows, largest first, each with how many of the people who have the tool used it this month.
 *
 * @param tools - The tool comparison.
 * @param usage - Each tool's usage this month and last, by lower-cased vendor id.
 * @param note - The note beneath a tool's bar: its take-up, and whether its seat fee is an estimate.
 * @returns The rows.
 */
export function toolRows(tools: ToolComparisonResponse[], usage: UsageComparison, note: (tool: ToolComparisonResponse) => string | null): RankedRow[] {
  const total = tools.reduce((sum, tool) => sum + (tool.totalSpend ?? 0), 0);
  return tools
    .filter((tool) => (tool.totalSpend ?? 0) > 0 || tool.activePersonCount > 0)
    .sort((a, b) => (b.totalSpend ?? 0) - (a.totalSpend ?? 0))
    .map((tool) => {
      const key = tool.vendorId.toLowerCase();
      return {
        key: tool.vendorId,
        label: tool.vendorDisplayName,
        spend: tool.totalSpend,
        sharePercent: shareOf(tool.totalSpend, total),
        people: tool.activePersonCount,
        perPerson: tool.totalSpend !== null && tool.activePersonCount > 0 ? tool.totalSpend / tool.activePersonCount : null,
        change: usage.previous ? changeBetween(usage.current.get(key), usage.previous.get(key)) : null,
        previousUsage: usage.previous?.get(key) ?? null,
        detail: note(tool),
      };
    });
}

/**
 * The person panel's rows, largest first, each noting the tool most of the person's spend went on.
 *
 * @param people - Everyone with spend.
 * @param vendors - The vendors, for their names.
 * @param mainTool - Turns the tool's name into the note.
 * @returns The rows.
 */
export function personRows(
  people: OrganisationPersonUsageRow[],
  vendors: OrganisationPersonUsageVendor[],
  mainTool: (toolName: string) => string,
): RankedRow[] {
  const total = people.reduce((sum, person) => sum + (person.totalSpend ?? 0), 0);
  const names = new Map(vendors.map((vendor) => [vendor.vendorId.toLowerCase(), vendor.vendorDisplayName]));
  return people
    .filter((person) => (person.totalSpend ?? 0) > 0)
    .sort((a, b) => (b.totalSpend ?? 0) - (a.totalSpend ?? 0))
    .map((person) => {
      const main = [...person.byVendor].sort((a, b) => (b.totalSpend ?? 0) - (a.totalSpend ?? 0))[0];
      return {
        key: person.personId,
        label: person.displayName.replace(/@rws\.com$/i, ""),
        spend: person.totalSpend,
        sharePercent: shareOf(person.totalSpend, total),
        people: null,
        perPerson: null,
        change: null,
        detail: main && (main.totalSpend ?? 0) > 0 ? mainTool(names.get(main.vendorId.toLowerCase()) ?? main.vendorId) : null,
        // The person panel is only shown to someone who may read named people, so each person opens their usage page.
        href: `/organization/people/${encodeURIComponent(person.personId)}`,
      };
    });
}

/**
 * The row whose usage rose most on last month, among rows whose usage last month was at least `minimumBase`; null when none rose.
 *
 * @param rows - The rows.
 * @param minimumBase - The smallest usage last month a rise is reported from.
 * @returns The row, or null.
 */
export function biggestRise(rows: RankedRow[], minimumBase = minimumRiseBase): RankedRow | null {
  return (
    rows
      .filter((row) => row.change !== null && row.change > 0 && (row.previousUsage ?? 0) >= minimumBase)
      .sort((a, b) => (b.change ?? 0) - (a.change ?? 0))[0] ?? null
  );
}

/**
 * The row with the highest spend per person, among rows with at least `minimumPeople` people (so one heavy user does not lead).
 *
 * @param rows - The rows.
 * @param minimumPeople - The fewest people a row needs.
 * @returns The row, or null.
 */
export function highestPerPerson(rows: RankedRow[], minimumPeople = 5): RankedRow | null {
  return rows.filter((row) => row.perPerson !== null && (row.people ?? 0) >= minimumPeople).sort((a, b) => (b.perPerson ?? 0) - (a.perPerson ?? 0))[0] ?? null;
}

/**
 * How concentrated the spend is: the share of it from the tenth of people who spent most.
 *
 * @param people - Everyone with spend.
 * @returns The number of people in that tenth and their share as a percentage, or null when there are too few people to say.
 */
export function topTenth(people: OrganisationPersonUsageRow[]): { people: number; sharePercent: number } | null {
  const spends = people.map((person) => person.totalSpend ?? 0).filter((spend) => spend > 0).sort((a, b) => b - a);
  if (spends.length < 10) {
    return null;
  }

  const count = Math.ceil(spends.length / 10);
  const total = spends.reduce((sum, spend) => sum + spend, 0);
  const top = spends.slice(0, count).reduce((sum, spend) => sum + spend, 0);
  return { people: count, sharePercent: total > 0 ? (top / total) * 100 : 0 };
}

/**
 * The share of the model spend that went on models whose name contains a word (such as Opus), and how many such models there are.
 *
 * @param rows - The model rows.
 * @param word - The word to look for, matched without regard to case.
 * @returns The share and the count, or null when no model's name contains it.
 */
export function modelFamilyShare(rows: RankedRow[], word: string): { sharePercent: number; count: number } | null {
  const matching = rows.filter((row) => row.label.toLowerCase().includes(word.toLowerCase()));
  return matching.length === 0 ? null : { sharePercent: matching.reduce((sum, row) => sum + row.sharePercent, 0), count: matching.length };
}

/** A tool that people pay for but do not use, this month. */
export interface IdleSeats {
  vendorId: string;
  displayName: string;
  /** People who paid for the tool and did not use it. */
  people: number;
  /** What those seats cost. */
  spend: number;
}

/**
 * The tools billed by the seat whose seats went unused this month, costliest first, from the organisation trend's month.
 *
 * @param trend - The trend, for the tools' names.
 * @param month - The month.
 * @returns One entry per tool with idle spend.
 */
export function idleSeats(trend: OrganisationTrendResponse, month: OrganisationTrendMonthResponse): IdleSeats[] {
  return month.vendors
    .filter((vendor) => vendor.idleSpend > 0)
    .map((vendor) => ({
      vendorId: vendor.vendorId,
      displayName: trend.vendors.find((candidate) => candidate.vendorId === vendor.vendorId)?.displayName ?? vendor.vendorId,
      people: Math.max(0, vendor.payingPeople - vendor.activePeople),
      spend: vendor.idleSpend,
    }))
    .sort((a, b) => b.spend - a.spend);
}

/**
 * The organisation's spend that no division holds: the people with spend who are not placed in the organisation chart.
 *
 * @param response - The organisation's spend.
 * @returns The spend and the people, or null when there is none.
 */
export function unclassifiedSpend(response: OrganisationSpendResponse): { spend: number; people: number; sharePercent: number } | null {
  const unit = response.byDivision.find((candidate) => candidate.unitLabel === unclassifiedLabel);
  const total = response.byDivision.reduce((sum, candidate) => sum + (candidate.totalSpend ?? 0), 0);
  return unit && (unit.totalSpend ?? 0) > 0 ? { spend: unit.totalSpend ?? 0, people: unit.personCount, sharePercent: shareOf(unit.totalSpend, total) } : null;
}

/**
 * A budgeted unit's name for a sentence: a group's with the unit it sits under, since many groups share a name ("General").
 *
 * @param report - The month's budget report, for the unit above.
 * @param unit - The unit.
 * @returns The name.
 */
export function budgetUnitName(report: BudgetReportResponse, unit: BudgetReportUnitResponse): string {
  const parent = unit.kind === "Group" && unit.parentId ? report.units.find((candidate) => candidate.id.toLowerCase() === unit.parentId?.toLowerCase()) : undefined;
  return parent ? `${parent.name} / ${unit.name}` : unit.name;
}

/** How the month's budgeted units stand against their budgets: the finding the Budgets panel and the hero lead with. */
export interface BudgetStanding {
  /** How many units have a budget, explicit or implied. */
  budgeted: number;
  /** How many of them are over it. */
  overCount: number;
  /** The unit furthest over its budget, and by how much; null when none is over. */
  furthestOver: { unit: BudgetReportUnitResponse; overBy: number } | null;
  /** The unit that has used the largest share of its budget, as a percentage; null when no unit has a budget above nothing. */
  mostUsed: { unit: BudgetReportUnitResponse; usedPercent: number } | null;
}

/**
 * How the month's budgeted units stand: how many are over, the one furthest over, and the one closest to its budget. Spend is
 * measured as the report counts it against the budget (`countedSpendUsd`), so Capex is left out where the setting says so.
 *
 * @param report - The month's budget report.
 * @returns The standing, or null when no unit has a budget.
 */
export function budgetStanding(report: BudgetReportResponse): BudgetStanding | null {
  const budgeted = report.units.filter((unit) => unit.budgetUsd !== null);
  if (budgeted.length === 0) {
    return null;
  }

  const over = budgeted.filter((unit) => unit.overBudget);
  const furthest = over.reduce<BudgetReportUnitResponse | null>(
    (worst, unit) => (worst === null || (unit.differenceUsd ?? 0) < (worst.differenceUsd ?? 0) ? unit : worst),
    null,
  );
  const mostUsed = budgeted
    .filter((unit) => (unit.budgetUsd ?? 0) > 0)
    .map((unit) => ({ unit, usedPercent: (unit.countedSpendUsd / (unit.budgetUsd ?? 1)) * 100 }))
    .reduce<{ unit: BudgetReportUnitResponse; usedPercent: number } | null>((best, candidate) => (best === null || candidate.usedPercent > best.usedPercent ? candidate : best), null);

  return {
    budgeted: budgeted.length,
    overCount: over.length,
    furthestOver: furthest ? { unit: furthest, overBy: -(furthest.differenceUsd ?? 0) } : null,
    mostUsed,
  };
}

/**
 * What each breakdown calls the server's unplaced bucket (`CostCentreDeriver.Unclassified`). Spend is placed through each person's
 * budget group (GH-135), so the bucket means something different in each: in no group at all, in a group with no Department, or in a
 * Department with no Division.
 */
export interface UnclassifiedLabels {
  division: string;
  department: string;
  group: string;
}

/**
 * Renames the server's unplaced bucket in each breakdown, so the viewer reads what it means there. Matching on the server's own
 * string (`unclassifiedSpend`, the overview's figures) still uses the raw label, so this is applied only to what is drawn.
 *
 * @param response - The organisation's spend as the server returned it.
 * @param labels - What to show for the bucket in each breakdown.
 * @returns The response with the bucket renamed in each breakdown.
 */
export function relabelUnclassified(response: OrganisationSpendResponse, labels: UnclassifiedLabels): OrganisationSpendResponse {
  const rename = (units: OrganisationUnitSpendResponse[], label: string): OrganisationUnitSpendResponse[] =>
    units.map((unit) => (unit.unitLabel === unclassifiedLabel ? { ...unit, unitLabel: label } : unit));
  return {
    ...response,
    byDivision: rename(response.byDivision, labels.division),
    byDepartment: rename(response.byDepartment, labels.department),
    byGroup: rename(response.byGroup, labels.group),
  };
}

/**
 * The daily series' copy of `relabelUnclassified`: the bucket's name in each day's units, for the three groupings it appears in.
 *
 * @param daily - The organisation's daily spend as the server returned it.
 * @param labels - What to show for the bucket in each grouping.
 * @returns The daily spend with the bucket renamed.
 */
export function relabelUnclassifiedDaily(daily: OrganisationDailySpendResponse, labels: UnclassifiedLabels): OrganisationDailySpendResponse {
  const series = (group: OrganisationDailySpendSeriesResponse, label: string): OrganisationDailySpendSeriesResponse => ({
    ...group,
    days: group.days.map((day) => ({
      ...day,
      units: day.units.map((unit) => (unit.unit === unclassifiedLabel ? { ...unit, unit: label } : unit)),
    })),
  });
  return {
    ...daily,
    byDivision: series(daily.byDivision, labels.division),
    byDepartment: series(daily.byDepartment, labels.department),
    byGroup: series(daily.byGroup, labels.group),
  };
}
